export type MockScenarioId =
  | 'past-departures'
  | 'duplicate-buses'
  | 'weird-line-names'
  | 'mixed-quality'
  | 'empty-list'
  | 'auth-401-once'
  | 'auth-401-always'
  | 'auth-endpoint-failure'
  | 'rate-limit'
  | 'network-offline'
  | 'server-error';

export type MockScenarioKind = 'bus-data' | 'transport-error' | 'auth-error';

export interface MockScenarioDefinition {
  id: MockScenarioId;
  label: string;
  kind: MockScenarioKind;
  description: string;
}

export interface MockScenarioState {
  useRealApi: boolean;
  scenarioId: MockScenarioId;
  latencyMs: number;
}

export const DEFAULT_MOCK_SCENARIO_STATE: MockScenarioState = {
  useRealApi: true,
  scenarioId: 'past-departures',
  latencyMs: 0
};

export const MOCK_SCENARIOS: MockScenarioDefinition[] = [
  {
    id: 'past-departures',
    label: 'Past departures',
    kind: 'bus-data',
    description: 'Mixes past and imminent buses so staleness filtering is exercised.'
  },
  {
    id: 'duplicate-buses',
    label: 'Duplicate buses',
    kind: 'bus-data',
    description: 'Repeats services with matching destinations to test duplicate collapsing.'
  },
  {
    id: 'weird-line-names',
    label: 'Weird line names',
    kind: 'bus-data',
    description: 'Injects odd casing, spacing, punctuation, and destination text.'
  },
  {
    id: 'mixed-quality',
    label: 'Mixed quality data',
    kind: 'bus-data',
    description: 'Combines duplicates, odd names, missing stands, and unsorted rows.'
  },
  {
    id: 'empty-list',
    label: 'Empty list',
    kind: 'bus-data',
    description: 'Returns no buses so the inactive state is visible.'
  },
  {
    id: 'auth-401-once',
    label: 'Auth 401 once',
    kind: 'transport-error',
    description: 'First bus-data request returns 401, retry succeeds after token refresh.'
  },
  {
    id: 'auth-401-always',
    label: 'Auth 401 always',
    kind: 'transport-error',
    description: 'Every bus-data request returns 401 to exercise auth backoff.'
  },
  {
    id: 'auth-endpoint-failure',
    label: 'Auth endpoint fails',
    kind: 'auth-error',
    description: 'Token refresh endpoint returns 500 and data requests remain unauthorized.'
  },
  {
    id: 'rate-limit',
    label: '429 rate limit',
    kind: 'transport-error',
    description: 'Returns 429 to exercise exponential rate-limit backoff.'
  },
  {
    id: 'network-offline',
    label: 'Offline / status 0',
    kind: 'transport-error',
    description: 'Simulates a cancelled or offline network request.'
  },
  {
    id: 'server-error',
    label: '500 server error',
    kind: 'transport-error',
    description: 'Returns a persistent upstream server failure.'
  }
];

export function getMockScenarioDefinition(id: MockScenarioId): MockScenarioDefinition {
  return MOCK_SCENARIOS.find((scenario) => scenario.id === id) ?? MOCK_SCENARIOS[0];
}
